import React from 'react';
import { Block, ListStyle, ParagraphFragment, RowFragment, SignatureStamp } from '../../config';
import HeadingBlock from '../../../Documents/components/Blocks/HeadingBlock';
import ParagraphBlock from '../../../Documents/components/Blocks/ParagraphBlock';
import ScopeListBlock from '../../../Documents/components/Blocks/ScopeListBlock';
import BulletListBlock from '../../../Documents/components/Blocks/BulletListBlock';
import TableBlock from '../../../Documents/components/Blocks/TableBlock';
import ImageBlock from '../../../Documents/components/Blocks/ImageBlock';
import SignatureBlock from '../../../Documents/components/Blocks/SignatureBlock';
import DividerBlock from '../../../Documents/components/Blocks/DividerBlock';
import SpacerBlock from '../../../Documents/components/Blocks/SpacerBlock';
import InstructionListBlock from '../../../Documents/components/Blocks/InstructionListBlock';
import DrawingListBlock from '../../../Documents/components/Blocks/DrawingListBlock';
import QaListBlock from '../../../Documents/components/Blocks/QaListBlock';

// =======================================
// INTERFACES
// =======================================

// Parsed block from pasted content
interface ParsedBlock {
  type: 'paragraph' | 'bullet_list' | 'heading';
  content: string;
  level?: number;
  listStyle?: ListStyle;
}

// Engineer type for signature block
interface Engineer {
  id: string;
  first_name: string;
  last_name: string;
}

interface BlockRendererProps {
  block: Block;
  onChange: (block: Block) => void;
  onDelete: () => void;
  isEditing: boolean;
  // Paragraph continued from / onto another page: this instance shows one piece
  fragment?: ParagraphFragment;
  /** Which rows of an instruction list or drawing list to show on this page. */
  rowFragment?: RowFragment;
  // Slash command props (for text blocks)
  onSlashCommand?: (position: { top: number; left: number }) => void;
  onSlashSearch?: (query: string) => void;
  onSlashClose?: () => void;
  slashSearchQuery?: string;
  isSlashMenuOpen?: boolean;
  // Focus/interaction props
  onFocus?: () => void;
  onBlur?: () => void;
  onEnter?: (currentContent: string, afterContent?: string) => void;
  onBackspaceEmpty?: () => void;
  /** A bullet list asking to be left: the items it keeps, then a paragraph. */
  onExitList?: (remainingItems: string[]) => void;
  // Smart paste
  onPasteMultiBlock?: (blocks: ParsedBlock[], currentContent: string) => void;
  // Signature block props
  signatoryId?: string | null;
  signatoryName?: string;
  engineers?: Engineer[];
  onSignatoryChange?: (id: string) => void;
  signatureStamp?: SignatureStamp | null;
}

// =======================================
// MAIN COMPONENT
// =======================================

const BlockRenderer: React.FC<BlockRendererProps> = ({ 
  block, 
  onChange, 
  onDelete,
  isEditing,
  fragment,
  rowFragment,
  onSlashCommand,
  onSlashSearch,
  onSlashClose,
  slashSearchQuery,
  isSlashMenuOpen,
  onFocus,
  onBlur,
  onEnter,
  onBackspaceEmpty,
  onExitList,
  onPasteMultiBlock,
  signatoryId,
  signatoryName,
  engineers,
  onSignatoryChange,
  signatureStamp
}) => {
  const handleChange = (data: Block['data']) => {
    onChange({ ...block, data });
  };

  // Common text editing props
  const textEditingProps = {
    onSlashCommand,
    onSlashSearch,
    onSlashClose,
    slashSearchQuery,
    isSlashMenuOpen,
    onFocus,
    onBlur,
    onEnter,
    onBackspaceEmpty,
    onPasteMultiBlock
  };

  switch (block.type) {
    case 'heading':
      return (
        <HeadingBlock
          block={block}
          onChange={handleChange}
          onDelete={onDelete}
          isEditing={isEditing}
          onEnter={onEnter}
        />
      );

    case 'paragraph':
      return (
        <ParagraphBlock
          block={block}
          onChange={handleChange}
          onDelete={onDelete}
          isEditing={isEditing}
          fragment={fragment}
          {...textEditingProps}
        />
      );

    case 'scope_list':
      return (
        <ScopeListBlock
          block={block}
          onChange={handleChange}
          onDelete={onDelete}
          isEditing={isEditing}
        />
      );

    case 'bullet_list':
      return (
        <BulletListBlock
          block={block}
          onChange={handleChange}
          onDelete={onDelete}
          isEditing={isEditing}
          onExitList={onExitList}
        />
      );

    case 'drawing_list':
      return (
        <DrawingListBlock
          block={block}
          onChange={handleChange}
          isEditing={isEditing}
          fragment={rowFragment}
        />
      );

    case 'qa_list':
      return (
        <QaListBlock
          block={block}
          onChange={handleChange}
          isEditing={isEditing}
          fragment={rowFragment}
        />
      );

    case 'table':
      return (
        <TableBlock
          block={block}
          onChange={handleChange}
          onDelete={onDelete}
          isEditing={isEditing}
        />
      );

    case 'image':
      return (
        <ImageBlock
          block={block}
          onChange={handleChange}
          onDelete={onDelete}
          isEditing={isEditing}
        />
      );

    case 'signature':
      return (
        <SignatureBlock
          block={block}
          onChange={handleChange}
          onDelete={onDelete}
          isEditing={isEditing}
          signatoryId={signatoryId}
          signatoryName={signatoryName}
          engineers={engineers}
          onSignatoryChange={onSignatoryChange}
          stamp={signatureStamp}
        />
      );

    case 'instruction_list':
      return (
        <InstructionListBlock
          block={block}
          onChange={handleChange}
          isEditing={isEditing}
          fragment={rowFragment}
        />
      );

    case 'divider':
      return (
        <DividerBlock
          block={block}
          onChange={handleChange}
          onDelete={onDelete}
          isEditing={isEditing}
        />
      );

    case 'spacer':
      return (
        <SpacerBlock
          block={block}
          onChange={handleChange}
          onDelete={onDelete}
          isEditing={isEditing}
        />
      );

    // Appendix-page blocks are drawn by their own page components in
    // DocumentEditor (and by DocumentPDF's appendix branches), never inside a
    // body canvas. Rendering nothing rather than "Unknown block type" keeps
    // them harmless should one ever reach a canvas.
    case 'table_appendix':
    case 'plan_appendix':
    case 'pdf_attachment':
      return null;

    // The blank page's marker is never placed on a page (the paginator turns
    // it into a page break and a label); nothing to draw if one ever were.
    case 'blank_page':
      return null;

    default:
      return (
        <div className="unknown-block">
          Unknown block type: {block.type}
        </div>
      );
  }
};

export default BlockRenderer;
